import { getCacheKey } from '@belong/api/hooks';
import {
  initOfferContentManager,
  IProductData,
  IProductLoadRequest,
  loadProductData
} from '@belong/common-nextjs-server-side-props';
import { offerContentManager } from '@belong/offers';
import type { TSSPChain } from '@belong/ssp-execution';
import { TPublicSSPContext } from './types';

const toSwrCache = (products: IProductData = {}, ...requests: IProductLoadRequest[]) => {
  const cache: Record<string, unknown> = {};

  return requests.reduce((acc, [productFamily, , journey]) => {
    const productData = products[productFamily];
    const cacheKey = getCacheKey(productFamily, journey);
    if (cacheKey && productData) {
      cache[cacheKey] = productData?.products;
    }

    return acc;
  }, cache);
};

/**
 * Fetch required product data, load offer content and apply offer-section overrides
 */
export const fetchProductsAndOffers =
  (...productsToLoad: IProductLoadRequest[]): TSSPChain<TPublicSSPContext> =>
  async ctx => {
    const correlationId = ctx.appServerSideProps?.correlationId;
    if (!correlationId) {
      return ctx;
    }

    // Load product and offers data for the requested products and initialise the offer content manager
    const products = await loadProductData(ctx.context.resolvedUrl, ...productsToLoad);
    await initOfferContentManager(products, correlationId);

    // Apply campaign overrides
    const sections = offerContentManager.applySectionOverrides(ctx.pageProps, ctx.pagePath, correlationId);

    return {
      ...ctx,
      appServerSideProps: {
        ...ctx.appServerSideProps,
        swrInitialCache: toSwrCache(products, ...productsToLoad)
      },
      pageProps: {
        ...ctx.pageProps,
        ...sections,
        offerContent: offerContentManager.export()
      }
    };
  };
